/**
 * Dashboard preferences store
 * Persists user's feature block visibility and order to localStorage
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const DEFAULT_VISIBLE = [
  'crm',
  'sale',
  'construction',
  'acceptance',
  'showroom',
  'stock',
  'bizcard',
  'approval',
  'ticket-requirement',
  'ticket-bug',
];

const DEFAULT_ORDER = [
  'crm',
  'sale',
  'construction',
  'acceptance',
  'showroom',
  'stock',
  'bizcard',
  'approval',
  'attendance',
  'leave',
  'ticket-requirement',
  'ticket-bug',
];

interface DashboardState {
  visibleBlockIds: string[];
  blockOrder: string[];
  isEditMode: boolean;
  setEditMode: (editing: boolean) => void;
  toggleBlock: (id: string) => void;
  moveBlock: (fromIndex: number, toIndex: number) => void;
  resetDefaults: () => void;
}

export const useDashboardStore = create<DashboardState>()(
  persist(
    (set) => ({
      visibleBlockIds: DEFAULT_VISIBLE,
      blockOrder: DEFAULT_ORDER,
      isEditMode: false,

      setEditMode: (editing) => set({ isEditMode: editing }),

      toggleBlock: (id) =>
        set((state) => ({
          visibleBlockIds: state.visibleBlockIds.includes(id)
            ? state.visibleBlockIds.filter((b) => b !== id)
            : [...state.visibleBlockIds, id],
        })),

      moveBlock: (fromIndex, toIndex) =>
        set((state) => {
          if (
            fromIndex < 0 ||
            fromIndex >= state.blockOrder.length ||
            toIndex < 0 ||
            toIndex >= state.blockOrder.length ||
            fromIndex === toIndex
          ) {
            return state;
          }
          const newOrder = [...state.blockOrder];
          const [moved] = newOrder.splice(fromIndex, 1);
          newOrder.splice(toIndex, 0, moved);
          return { blockOrder: newOrder };
        }),

      resetDefaults: () =>
        set({
          visibleBlockIds: DEFAULT_VISIBLE,
          blockOrder: DEFAULT_ORDER,
        }),
    }),
    {
      name: 'saiens-dashboard',
      version: 2,
      partialize: (state) => ({
        visibleBlockIds: state.visibleBlockIds,
        blockOrder: state.blockOrder,
      }),
      migrate: (_persisted, version) => {
        if (version < 2) {
          // v2: added showroom + stock blocks
          // Reset to new defaults so existing users see the new blocks
          return {
            visibleBlockIds: DEFAULT_VISIBLE,
            blockOrder: DEFAULT_ORDER,
          };
        }
        return _persisted as Record<string, unknown>;
      },
    },
  ),
);
